/**
 * Admin Module Registry API
 *
 * Wraps /api/admin/modules — the GLOBAL module catalog and the platform policy
 * governing it. This is the registry half of the registry-vs-channel split:
 *
 *   /api/modules        per-channel, per-user — enable/configure a module FOR
 *                       a channel. Consumed by pages/Modules.tsx.
 *   /api/admin/modules  who may enable it at all, whether it's globally killed,
 *                       and the admin-controlled settings baseline. Here.
 *
 * Every route is super-admin only, and every mutating route returns the FULL
 * refreshed RegistryEntry. That is the load-bearing property of this surface:
 * a caller replaces its row from the response and never needs a follow-up GET,
 * so the list can't drift from the server between a write and a redraw.
 *
 * Consumed by pages/AdminModules.tsx.
 */

import api from '@/api/client'

/** Who is allowed to enable a module for their channels. */
export type ModuleAvailability = 'admin_only' | 'everyone' | 'specific_users'

export const AVAILABILITY_OPTIONS: {
  value: ModuleAvailability
  label: string
  help: string
}[] = [
  {
    value: 'everyone',
    label: 'Everyone',
    help: 'Any user can enable this for their channels.',
  },
  {
    value: 'admin_only',
    label: 'Admin only',
    help: 'Only a super admin can enable it. Everyone else is refused.',
  },
  {
    value: 'specific_users',
    label: 'Specific users',
    help: 'Only users granted access below can enable it.',
  },
]

/**
 * A user holding a specific-user access grant.
 *
 * `id` is the internal users.id — NOT the Twitch id the JWT carries. The two
 * are never interchangeable; see the footgun note in CONTEXT.md.
 */
export interface GrantedUser {
  id: number
  twitch_username: string
}

/** A module's global registry state, as the admin sees it. */
export interface RegistryEntry {
  name: string
  display_name: string
  description: string | null
  version: string | null
  availability: ModuleAvailability
  /** Global kill switch. False disables the module for everyone, including admins. */
  enabled: boolean
  /** Currently loaded by the runtime loader. Reload requires this. */
  loaded: boolean
  /** Present on disk as a discoverable module. */
  discovered: boolean
  enabled_channel_count: number
  /**
   * `provides_capabilities` is ENFORCED at load since R20 chat 2: a module's
   * registration of anything it did not declare is refused (the registration,
   * not the module). `consumes_capabilities` is advisory — the loader reports
   * undeclared consumption, including through framework hooks, but refuses
   * nothing. See architecture/module-platform.md section C.
   */
  provides_capabilities: string[]
  consumes_capabilities: string[]
  granted_users: GrantedUser[]
  /** A ModuleGlobalSettings row exists. Distinct from "settings is empty". */
  has_global_settings: boolean
  global_settings: Record<string, unknown>
  user_overridable_keys: string[]
}

/** Partial update of availability and/or the global kill switch. */
export interface RegistryPatch {
  availability?: ModuleAvailability
  enabled?: boolean
}

/** The admin settings baseline plus the keys a channel owner may override. */
export interface GlobalSettingsUpdate {
  settings: Record<string, unknown>
  user_overridable_keys: string[]
}

/** A registered user, from GET /api/admin/users. */
export interface AdminUser {
  id: number
  twitch_id: string
  twitch_username: string
  is_super_admin: boolean
  is_active: boolean
}

/** POST /api/modules/{name}/reload */
export interface ModuleReloadResult {
  status: string
  module: string
  version: string | null
}

export const adminModulesApi = {
  /**
   * The whole catalog: every registry row plus any module discovered on disk
   * that has no row yet (reported with permissive defaults, and written for
   * real the first time it's patched).
   */
  list(): Promise<RegistryEntry[]> {
    return api.get<RegistryEntry[]>('/api/admin/modules')
  },

  get(name: string): Promise<RegistryEntry> {
    return api.get<RegistryEntry>(`/api/admin/modules/${encodeURIComponent(name)}`)
  },

  /** Set availability tier and/or the kill switch. Creates the row on first write. */
  patch(name: string, data: RegistryPatch): Promise<RegistryEntry> {
    return api.patch<RegistryEntry>(
      `/api/admin/modules/${encodeURIComponent(name)}`,
      data,
    )
  },

  /**
   * Replace the global settings baseline and the overridable-key list.
   *
   * A full replace, not a merge — omitting a key deletes it. Also the moment
   * locking begins: once this row exists, a channel may only write keys named
   * in user_overridable_keys, and everything else answers 403. Before it
   * exists, all keys are overridable (the legacy-preserving rule).
   */
  setGlobalSettings(
    name: string,
    data: GlobalSettingsUpdate,
  ): Promise<RegistryEntry> {
    return api.put<RegistryEntry>(
      `/api/admin/modules/${encodeURIComponent(name)}/global-settings`,
      data,
    )
  },

  /** Grant specific-user access. Idempotent. */
  grantAccess(name: string, userId: number): Promise<RegistryEntry> {
    return api.post<RegistryEntry>(
      `/api/admin/modules/${encodeURIComponent(name)}/access/${userId}`,
    )
  },

  /** Revoke it. Idempotent — revoking a grant that isn't there is not an error. */
  revokeAccess(name: string, userId: number): Promise<RegistryEntry> {
    return api.delete<RegistryEntry>(
      `/api/admin/modules/${encodeURIComponent(name)}/access/${userId}`,
    )
  },

  /**
   * Every registered user, for resolving and picking grant targets.
   *
   * Lives on /api/admin (not /api/admin/modules) because it isn't module state;
   * the registry needs it only because grants key on internal user ids that no
   * other surface exposes.
   */
  listUsers(): Promise<AdminUser[]> {
    return api.get<AdminUser[]>('/api/admin/users')
  },

  /**
   * Hot-reload a module. Super-admin only; affects every channel using it.
   *
   * On the PER-CHANNEL router, not the admin one — the only registry action
   * that isn't under /api/admin/modules. 404s unless the module is currently
   * LOADED, so callers must gate on entry.loaded: a discovered-but-unloaded
   * module (one that failed to import) cannot be reloaded this way and needs a
   * service restart.
   *
   * Broadcasts module.reloaded over the shared WebSocket on success.
   */
  reload(name: string): Promise<ModuleReloadResult> {
    return api.post<ModuleReloadResult>(
      `/api/modules/${encodeURIComponent(name)}/reload`,
    )
  },
}

export default adminModulesApi
